import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { catchError, map, Observable, of, Subject, tap, throwError } from 'rxjs';
import { DownloadBlob } from '../download/model/downloadBlob';
import { DateTime } from 'luxon';
import { ErrorLogService } from '@shared/services/errorhandler/error-log.service';

interface LogbookDB extends DBSchema {
  exportFiles: {
    key: string;
    value: DownloadBlob;
  };
}

@Injectable({
  providedIn: 'root'
})

export class FiledownloadService implements OnDestroy {

  
  private indexFileDB$?: Promise<IDBPDatabase<LogbookDB>>;
  destroy$ = new Subject<void>();
  lastDownloadTime: string | null = null;

  constructor(
    private http: HttpClient,
    private errorLogService: ErrorLogService
  ) {}

  onInit(): void {
    this.lastDownloadTime = localStorage.getItem('lastDownloadTime');
  }
  
  initDB(): void {
    this.indexFileDB$ = openDB<LogbookDB>('Logbook_DB', 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('exportFiles')) {
          db.createObjectStore('exportFiles');
        }
      }
    });
  }

  /**
   * Downloads a file as a Blob from the specified URL and processes it.
   *
   * @param empNum - The employee number associated with the download request.
   * @param url - The URL from which to download the file.
   * @param key - The key or filename to associate with the downloaded file.
   * @returns An Observable emitting a `DownloadBlob` object containing the file data and metadata.
   *
   * @remarks
   * - Saves the downloaded file using `saveFile` and updates the last download time in local storage.
   * - Logs errors to the error log service if saving fails or if an HTTP error occurs.
   * - Handles HTTP 429 (Too Many Requests) errors specifically, logging a rate limit message.
   * - Throws an error if no blob is returned from the server.
   */
  downloadFile(empNum: string, url: string, key: string): Observable<DownloadBlob> {
    return this.http.get(url, { responseType: 'blob' }).pipe(
      map((blob) => {
        if (!blob) {
          this.errorLogService.logError(`[ERROR] - ${DateTime.now().toString()}: 
          Export: downloadFile() - No blob returned from Server`);
          throw new Error('No blob returned from server');
        }
        const blobFile: DownloadBlob = {
          fileName: key,
          blobName: blob,
          timestamp: DateTime.now().setZone('utc').toISO() || ''
        };
        this.saveFile(key, blobFile).then(() => {
          this.lastDownloadTime = DateTime.now().toISO() || '';
          localStorage.setItem('lastDownloadTime', this.lastDownloadTime);
        }).catch(err => {
          console.error('Failed to save CSV:', err);
          this.errorLogService.logError(`[ERROR] - ${DateTime.now().toString()}: 
          Failed to save CSV file to IndexedDB - ${err}`);
        });
        return blobFile;
      }),
      catchError((error: HttpErrorResponse | Error) => {
        if (error instanceof HttpErrorResponse && error.status === 429) {
          console.error('Too Many Requests: You have hit the rate limit.');
          this.errorLogService.logError(`[ERROR] - ${DateTime.now().toString()}: 
            Too Many Export Download Requests - Rate limit exceeded.`);
        } else {
          console.error('An error occurred:', error);
          this.errorLogService.logError(`[ERROR] - FiledownloadService downloadFileCSV${DateTime.now().toString()}: 
            HTTP Error - ${error instanceof Error ? error.message : ''}`);
        }
        return throwError(() => error);
      })
    )
  }
  
  async saveFile(key: string, downloadBlob: DownloadBlob): Promise<void> {
    const db = await this.indexFileDB$;
    await db?.put('exportFiles', downloadBlob, key);
  }

  // Get all files from IndexedDB that are CSV files
  // ? Future use for PDF files as well
  async getAllExportFiles(): Promise<DownloadBlob[] | undefined> {
    const db = await this.indexFileDB$;
    if (!db) return [];
    const allFiles: DownloadBlob[] = [];
    let cursor = await db.transaction('exportFiles').store.openCursor();
    while (cursor) {
      const fileName = cursor.value.fileName;
      if (fileName.toLowerCase().endsWith('.csv')) {
        allFiles.push(cursor.value);
      }
      cursor = await cursor.continue();
    }
    return allFiles;
  }

  // Get all map files from IndexedDB, excluding CSV files
  async getAllMapFiles(): Promise<DownloadBlob[] | undefined> {
    const db = await this.indexFileDB$;
    if (!db) return [];
    const mapFiles: DownloadBlob[] = [];
    let cursor = await db.transaction('exportFiles').store.openCursor();
    while (cursor) {
      const fileName = cursor.value.fileName;
      if (!fileName.toLowerCase().endsWith('.csv')) {
        mapFiles.push(cursor.value);
      }
      cursor = await cursor.continue();
    }
    return mapFiles;
  }

  // getAllMapFiles2(): Observable<DownloadBlob[]> {
  //   return new Observable<DownloadBlob[]>(observer => {
  //     this.indexFileDB$?.then(db => {
  //         if (!db) {
  //           observer.next([]);
  //           observer.complete();
  //           return;
  //         }
  //         const mapFiles: DownloadBlob[] = [];
  //         db.transaction('exportFiles').store.openCursor().then(function iterate(cursor) {
  //           if (!cursor) {
  //             observer.next(mapFiles);
  //             observer.complete();
  //             return;
  //           }
  //           const fileName = cursor.value.fileName;
  //           if (!fileName.toLowerCase().endsWith('.csv')) {
  //             mapFiles.push(cursor.value);
  //           }
  //           cursor.continue().then(iterate);
  //         }).catch(err => {
  //           observer.error(err);
  //         });
  //       })
  //       .catch(err => {
  //         observer.error(err);
  //       });
  //   });
  // }
  // Get a single file from IndexedDB
  async getFile(key: string): Promise<DownloadBlob | undefined> {
    const db = await this.indexFileDB$;
    return db?.get('exportFiles', key);
  }

  // Check if a blob exists in IndexedDB
  async blobExists(key: string): Promise<boolean> {
    const db = await this.indexFileDB$;
    const result = await db?.get('exportFiles', key);
    
    return result !== undefined;
  }

  // Delete a single file from IndexedDB
  async deleteBlob(key: string): Promise<void> {
    if (!this.indexFileDB$) {
      this.initDB();
    }
    const db = await this.indexFileDB$;
    if (db) {
      await db.delete('exportFiles', key);
      const exists = await db.get('exportFiles', key);
      if (exists === undefined) {
        console.log(`Blob with key "${key}" successfully deleted.`);
      } else {
        this.errorLogService.logError(`[ERROR] - ${DateTime.now().toString()}: 
          Failed to delete blob with key "${key}".`);
        console.error(`Failed to delete blob with key "${key}".`);
      }
    } else {
      this.errorLogService.logError(`[ERROR] - ${DateTime.now().toString()}: IndexedDB is not initialized.`);
      console.error('IndexedDB is not initialized.');
    }
  }

  // Delete all files by employee number, called when resetting emulated pilot
  deleteBlobByEmpNum(empNum: string): void {
    if (!this.indexFileDB$) {
      this.initDB();
    }
    const request = indexedDB.open('Logbook_DB', 1);
    request.onsuccess = (event) => {
      const db = request.result;
      const transaction = db.transaction('exportFiles', 'readwrite');
      Array.from(db.objectStoreNames).forEach((storeName) => {
        const objectStore = transaction.objectStore(storeName);
        const getAllKeysRequest = objectStore.getAllKeys();

        getAllKeysRequest.onsuccess = () => {
          const keys = getAllKeysRequest.result as string[];
          keys
            .filter(key => typeof key === 'string' && key.startsWith(empNum))
            .forEach(key => {
              const deleteRequest = objectStore.delete(key);
              deleteRequest.onsuccess = () => {
                console.log(`Deleted file with key: ${key}`);
              };
              deleteRequest.onerror = (err) => {
                console.error(`Failed to delete file with key: ${key}`, err);
              };
            });
        };
        getAllKeysRequest.onerror = (err) => {
          console.error(`Failed to get keys for store: ${storeName}`, err);
        };
      }); 
    } 
  }

  // Delete all files in the IndexedDB, called from AdminComponent
  deleteAllBlobs(): void {
    if (!this.indexFileDB$) {
      this.initDB();
    }
  
    const request = indexedDB.open('Logbook_DB', 1);
    request.onsuccess = (event) => {
      const db = request.result;
      const transaction = db.transaction('exportFiles', 'readwrite');
      Array.from(db.objectStoreNames).forEach((storeName) => {
        const objectStore = transaction.objectStore(storeName);
        const clearRequest = objectStore.clear();
    
        clearRequest.onsuccess = () => {
          console.log(`Cleared store: ${storeName}`);
        };
        clearRequest.onerror = (err) => {
          console.error(`Failed to clear store: ${storeName}`, err);
        };
      }); 
    }
  }

  closeIndexedDB(): void {
    if (this.indexFileDB$) {
      this.indexFileDB$.then(db => {
        if (db) {
          db.close();
          console.log('IndexedDB connection closed');
        } else {
          console.error('No IndexedDB connection to close.');
        }
      }).catch(err => {
        console.error('Error closing IndexedDB:', err);
      });
    }
  }
  

  getContentofBlob(key: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('Logbook_DB', 1);
      request.onsuccess = () => {
        const db = request.result;
        const transaction = db.transaction('exportFiles', 'readonly');
        const objectStore = transaction.objectStore('exportFiles');
        const getRequest = objectStore.get(key);
        getRequest.onsuccess = (event) => {
          const result = getRequest.result;
          if (result && result.blobName instanceof Blob) {
            const reader = new FileReader();
            reader.onload = () => {
              resolve(reader.result as string);
            };
            reader.onerror = (err) => {
              reject(err);
            };
            reader.readAsText(result.blobName);
          } else {
            reject('No blob found for the given key');
          }
        };
        getRequest.onerror = (err) => {
          reject(err);
        };
      };
      request.onerror = (err) => {
        reject(err);
      };
    });
  }

  getCurrentMonthDates(): {start: string, end: string} {
    const startOfMonth = DateTime.now().startOf('month').setZone('utc');
    const endOfMonth = startOfMonth.endOf('month');
    return {start: startOfMonth.toFormat('yyyy-MM-dd') || '', end: endOfMonth.toFormat('yyyy-MM-dd') || ''};
  }

  getCurrentYearDates(): {start: string, end: string} {
    const startOfYear = DateTime.now().startOf('year').setZone('utc');
    const endOfYear = startOfYear.endOf('year');
    return {start: startOfYear.toFormat('yyyy-MM-dd') || '', end: endOfYear.toFormat('yyyy-MM-dd') || ''};
  }

  getLastSixMonthsDates(): {start: string, end: string} {
    const today = DateTime.now().setZone('utc');
    const startOfSixMonthsAgo = today.minus({ months: 6 });
    return {
      start: startOfSixMonthsAgo.toFormat('yyyy-MM-dd') || '',
      end: today.toFormat('yyyy-MM-dd') || ''
    };
  }

  getLastTwelveMonthsDates(): {start: string, end: string} {
    const today = DateTime.now().setZone('utc');
    const startOfTwelveMonthsAgo = today.minus({ months: 12 });
    return {
      start: startOfTwelveMonthsAgo.toFormat('yyyy-MM-dd') || '',
      end: today.toFormat('yyyy-MM-dd') || ''
    };
  }

  getDateRanges(date: Date[]): {start: string, end: string} {
    const startOfDay = DateTime.fromJSDate(date[0]).setZone('utc');
    const endOfDay = DateTime.fromJSDate(date[1]).setZone('utc');
    return {
      start: startOfDay.toFormat('yyyy-MM-dd') || '',
      end: endOfDay.toFormat('yyyy-MM-dd') || ''
    };
    return {start: '', end: ''};
  }

  ngOnDestroy(): void {
    this.indexFileDB$;
  }

}
